import type { ChatConversation, ChatMessage, DashboardSummary, PageContext } from '../types/assistant';

function getCSRFToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute('content') || '' : '';
}

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'X-CSRF-Token': getCSRFToken(),
  };
  // DeviseTokenAuth headers from localStorage
  const accessToken = localStorage.getItem('access-token');
  const client = localStorage.getItem('client');
  const uid = localStorage.getItem('uid');
  if (accessToken && client && uid) {
    headers['access-token'] = accessToken;
    headers['client'] = client;
    headers['uid'] = uid;
  }
  return headers;
}

const BASE_URL = '/api/v1/employer/chat';

export async function listConversations(): Promise<{ conversations: ChatConversation[] }> {
  const res = await fetch(`${BASE_URL}/conversations`, {
    headers: { ...getAuthHeaders(), 'Accept': 'application/json' },
    credentials: 'same-origin',
  });
  if (!res.ok) throw new Error(`Failed to list conversations: ${res.status}`);
  return res.json();
}

export async function createConversation(title?: string): Promise<ChatConversation> {
  const res = await fetch(`${BASE_URL}/conversations`, {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json', 'Accept': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ title }),
  });
  if (!res.ok) throw new Error(`Failed to create conversation: ${res.status}`);
  return res.json();
}

export async function getConversation(id: number): Promise<{ conversation: ChatConversation; messages: ChatMessage[] }> {
  const res = await fetch(`${BASE_URL}/conversations/${id}`, {
    headers: { ...getAuthHeaders(), 'Accept': 'application/json' },
    credentials: 'same-origin',
  });
  if (!res.ok) throw new Error(`Failed to get conversation: ${res.status}`);
  return res.json();
}

export async function archiveConversation(id: number): Promise<void> {
  const res = await fetch(`${BASE_URL}/conversations/${id}`, {
    method: 'DELETE',
    headers: { ...getAuthHeaders(), 'Accept': 'application/json' },
    credentials: 'same-origin',
  });
  if (!res.ok) throw new Error(`Failed to archive conversation: ${res.status}`);
}

export function sendMessageStream(
  conversationId: number,
  message: string,
  context?: PageContext,
  intent?: string
): { abort: () => void; stream: Promise<void> } {
  const controller = new AbortController();

  const stream = (async () => {
    const res = await fetch(`${BASE_URL}/conversations/${conversationId}/messages`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
      },
      credentials: 'same-origin',
      body: JSON.stringify({
        message,
        context,
        intent,
      }),
      signal: controller.signal,
    });

    if (!res.ok) throw new Error(`Failed to send message: ${res.status}`);
    if (!res.body) throw new Error('No response body');

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          try {
            const event = JSON.parse(data);
            window.dispatchEvent(new CustomEvent('didi:stream', { detail: event }));
          } catch {
            // skip malformed events
          }
        }
      }
    }
  })();

  return {
    abort: () => controller.abort(),
    stream,
  };
}

export async function confirmAction(actionId: number): Promise<Record<string, unknown>> {
  const res = await fetch(`${BASE_URL}/actions/${actionId}/confirm`, {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json', 'Accept': 'application/json' },
    credentials: 'same-origin',
  });
  if (!res.ok) throw new Error(`Failed to confirm action: ${res.status}`);
  return res.json();
}

export async function cancelAction(actionId: number): Promise<Record<string, unknown>> {
  const res = await fetch(`${BASE_URL}/actions/${actionId}/cancel`, {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json', 'Accept': 'application/json' },
    credentials: 'same-origin',
  });
  if (!res.ok) throw new Error(`Failed to cancel action: ${res.status}`);
  return res.json();
}

export async function getDashboardSummary(): Promise<{ summary: DashboardSummary }> {
  const res = await fetch(`${BASE_URL}/summary`, {
    headers: { ...getAuthHeaders(), 'Accept': 'application/json' },
    credentials: 'same-origin',
  });
  if (!res.ok) throw new Error(`Failed to get summary: ${res.status}`);
  return res.json();
}
